import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import Loader from '../../components/common/Loader';
import type { CompanyItemDataFragment } from '../../pages/CompanyManagementPage/shared/Companies.api';
import { useFetchCompanyDetailsQuery } from '../../pages/CompanyManagementPage/shared/Companies.api';
import extractFromJWT from '../../utils/extractFromJWT';
import { getToken, onTokenUpdate } from '../../utils/session';
import type { CurrentUserFragment, RoleAssignmentFragment } from './Usersession.api';
import { useFetchCurrentUserQuery } from './Usersession.api';
import useSessionLifecycle from './useSessionLifecycle';

export interface ContextValue {
    token: string | null;
    user: CurrentUserFragment | null;
    activeRole: RoleAssignmentFragment | null;
    sessionId: string | null;
    selectedCompanyId: string | null;
    company: CompanyItemDataFragment | null;
    logout: () => void;
}

export const Context = createContext<ContextValue | null>(null);

export const useUserSession = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error('useUserSession must be used within a UserSessionProvider');
    }

    return context;
};

export function useUser(shouldThrowIfNull: true): CurrentUserFragment;
export function useUser(shouldThrowIfNull: false | undefined): CurrentUserFragment | null;
export function useUser(shouldThrowIfNull?: boolean): CurrentUserFragment | null;
export function useUser(shouldThrowIfNull = false): CurrentUserFragment | null {
    const { user } = useUserSession(); // Assuming useUserSession is correctly defined and imported

    if (shouldThrowIfNull && !user) {
        throw new Error('User is not available');
    }

    return user;
}

// this function returns the active role assignment of the user. Superadmin
export function useActiveRole(): RoleAssignmentFragment | null {
    const { activeRole } = useUserSession();

    return activeRole;
}

// this function can be used when logged in user is super admin or tenant user
export function useGetCompanyId(): string | null {
    const user = useUser(true);
    const { selectedCompanyId } = useUserSession();

    if (user.__typename === 'TenantUser' && user.company) {
        return user.company.id;
    }

    return selectedCompanyId;
}

// this function is used when logged in user is super admin
export function useSelectedCompanyId(): string | null {
    const { selectedCompanyId } = useUserSession();
    return selectedCompanyId;
}

// returns the full company object from the session context (works for both super admins and tenant users)
export function useSelectedCompany(): { company: CompanyItemDataFragment | null; isDemo: boolean } {
    const { company } = useUserSession();
    return { company, isDemo: company?.isDemo ?? false };
}

export interface UserSessionProviderProps {
    children: ReactNode;
}

const UserSessionProvider = ({ children }: UserSessionProviderProps) => {
    // get the initial token from the session
    // forcefully run validation when retrieving it
    const [currentToken, setCurrentToken] = useState(() => getToken(true));

    const sessionId = useMemo(() => {
        if (currentToken) {
            return (extractFromJWT(currentToken) as { sessionId: { $oid: string } }).sessionId.$oid as string;
        }

        return null;
    }, [currentToken]);

    const selectedCompanyId = useMemo(() => {
        if (currentToken) {
            try {
                const payload = extractFromJWT<{ selectedCompanyId?: { $oid: string } }>(currentToken);
                return payload.selectedCompanyId?.$oid || null;
            } catch {
                return null;
            }
        }
        return null;
    }, [currentToken]);

    // listen for token updates and update the state
    useEffect(() => onTokenUpdate(setCurrentToken), [setCurrentToken]);

    // session lifecycle: activity tracking, inactivity warning, conditional refresh, logout
    const { logout } = useSessionLifecycle(currentToken);

    // query to fetch user document
    const { data, refetch, loading } = useFetchCurrentUserQuery({
        // use network only policy to avoid returning stale data
        fetchPolicy: 'cache-and-network',
    });

    // Derive companyId for both tenant users and super admins
    const user = currentToken ? data?.currentUser?.user || null : null;
    const companyId = useMemo(() => {
        if (user?.__typename === 'TenantUser' && user.company) {
            return user.company.id;
        }
        return selectedCompanyId;
    }, [user, selectedCompanyId]);

    // Fetch full company object once, shared via context
    const { data: companyData, loading: companyLoading } = useFetchCompanyDetailsQuery({
        variables: { id: companyId! },
        skip: !companyId,
        fetchPolicy: 'cache-and-network',
    });

    const company = useMemo(() => {
        const c = companyData?.company ?? null;
        // Avoid stale data from a previous company while the new query is loading
        if (c && c.id !== companyId) {
            return null;
        }
        return c;
    }, [companyData, companyId]);

    // we want to refetch the user document whenever the token changed
    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentToken]);

    const context = useMemo(
        (): ContextValue => ({
            token: currentToken,
            // if there's no token then we right away nullify the user document
            user: currentToken ? data?.currentUser?.user || null : null,
            activeRole: currentToken ? data?.currentUser?.activeRole || null : null,
            sessionId,
            selectedCompanyId,
            company,
            logout,
        }),
        [currentToken, data, sessionId, selectedCompanyId, company, logout]
    );

    if (context.token && !context?.user && loading) {
        return <Loader />;
    }

    if (context.token && context.user && companyId && !company && companyLoading) {
        return <Loader />;
    }

    return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default UserSessionProvider;
